
(function () {
  'use strict';

  function initStickyHeader(header) {
    var offset = parseInt(header.dataset.stickyOffset, 10) || 0;
    // Pages without the dark homepage hero keep the solid (white) header at
    // all times - see data-always-solid in header-v2.liquid.
    var alwaysSolid = header.dataset.alwaysSolid === 'true';
    var ticking = false;

    function update() {
      var scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      header.classList.toggle('is-scrolled', alwaysSolid || scrollY > offset);
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    update(); // set correct state on load (e.g. page refreshed mid-scroll)
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  function initMenuPanel(header) {
    var btn = header.querySelector('#header-v2-menu-btn');
    var panel = header.querySelector('#header-v2-menu-panel');
    if (!btn || !panel) return;

    var backdrop = header.querySelector('.header-v2__backdrop');
    var closeBtn = panel.querySelector('.header-v2__drawer-close');
    var mobileQuery = window.matchMedia('(max-width: 767px)');

    function isOpen() {
      return panel.classList.contains('is-open');
    }

    // Drawer mode (mobile) also dims the page and locks its scroll.
    function setPageLock(locked) {
      document.documentElement.style.overflow = locked ? 'hidden' : '';
      if (backdrop) backdrop.classList.toggle('is-open', locked);
    }

    function close(returnFocus) {
      if (!isOpen()) return;
      panel.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
      setPageLock(false);
      if (returnFocus) btn.focus();
    }

    function open() {
      panel.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      if (mobileQuery.matches) {
        setPageLock(true);
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      }
    }

    btn.addEventListener('click', function (event) {
      event.stopPropagation();
      if (isOpen()) {
        close();
      } else {
        open();
      }
    });

    // Drawer close button and backdrop.
    header.querySelectorAll('[data-menu-close]').forEach(function (el) {
      el.addEventListener('click', function (event) {
        event.preventDefault();
        close(true);
      });
    });

    // Any menu link closes the menu — needed for same-page anchor links
    // (#faq etc.), where the page doesn't reload to reset it.
    panel.addEventListener('click', function (event) {
      if (event.target.closest('a')) close();
    });

    document.addEventListener('click', function (event) {
      if (!header.contains(event.target)) close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen()) close(true);
    });

    // Rotating a phone to landscape / resizing past mobile while the drawer is
    // open: drop the scroll lock and backdrop so the page isn't stuck.
    var onBreakpoint = function () {
      if (!mobileQuery.matches) close();
    };
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', onBreakpoint);
    } else if (mobileQuery.addListener) {
      mobileQuery.addListener(onBreakpoint);
    }
  }

  // ---------------------------------------------------------------------------
  // Adventure section: angle viewer. Each colourway is a set of pre-rendered
  // cut-outs (front, front 3/4, back 3/4, back) stacked in the stage; dragging
  // walks the set so it reads as one continuous turn.
  //
  // Every frame of every colourway is already in the DOM as an <img>, so a drag
  // or a colour change is a class toggle with nothing to fetch. Only the first
  // frame is eager; the rest are loading="lazy" and arrive as the section nears
  // the viewport.
  //
  // Deliberately not a 3D model: <model-viewer> cost ~1MB of library plus 1.4MB
  // per .glb, and only ever showed these same angles.
  // ---------------------------------------------------------------------------

  function initAdventureViewer(section) {
    var stage = section.querySelector('[data-tuko-stage]');
    var hint = section.querySelector('[data-tuko-hint]');
    var nameEl = section.querySelector('[data-tuko-colorway-name]');
    var swatches = [].slice.call(section.querySelectorAll('.tuko-adventure__swatch'));
    var frames = stage ? [].slice.call(stage.querySelectorAll('[data-tuko-frame]')) : [];

    if (!stage) return;

    // Frames are laid out colourway-major in the markup, so index arithmetic is
    // enough — no need to query per change.
    var frameCount = parseInt(stage.dataset.frameCount, 10) || 0;
    var colorwayCount = frameCount ? frames.length / frameCount : 0;
    var dragStep = parseInt(stage.dataset.dragStep, 10) || 55;

    var frame = 0;
    var colorway = 0;

    function show() {
      if (!frameCount) return;

      var active = colorway * frameCount + frame;
      frames.forEach(function (img, index) {
        img.classList.toggle('is-shown', index === active);
      });
    }

    // --- Colourway ----------------------------------------------------------

    function selectColorway(index, focus) {
      var swatch = swatches[index];
      if (!swatch) return;

      swatches.forEach(function (other, otherIndex) {
        var isActive = otherIndex === index;
        other.classList.toggle('is-active', isActive);
        other.setAttribute('aria-checked', isActive ? 'true' : 'false');
        other.tabIndex = isActive ? 0 : -1;
      });

      if (nameEl && swatch.dataset.label) nameEl.textContent = swatch.dataset.label;
      if (focus) swatch.focus();

      // The angle carries over: the visitor keeps looking at the same side.
      if (index < colorwayCount) {
        colorway = index;
        show();
      }
    }

    swatches.forEach(function (swatch, index) {
      swatch.addEventListener('click', function () {
        selectColorway(index);
      });

      // Arrow keys move between radios, matching native radio group behaviour.
      swatch.addEventListener('keydown', function (event) {
        var step = 0;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') step = 1;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') step = -1;
        if (!step) return;
        event.preventDefault();
        selectColorway((index + step + swatches.length) % swatches.length, true);
      });
    });

    if (!frames.length || !frameCount) return;

    // --- Angle --------------------------------------------------------------

    function setFrame(index) {
      // Wraps, so a drag past the last angle comes back round to the front.
      frame = ((index % frameCount) + frameCount) % frameCount;
      show();
    }

    function markTouched() {
      if (hint) hint.classList.add('is-hidden');
    }

    // --- Drag / swipe -------------------------------------------------------

    var dragging = false;
    var startX = 0;
    var startFrame = 0;
    var pointerId = null;

    stage.addEventListener('pointerdown', function (event) {
      if (event.button !== undefined && event.button !== 0) return;
      dragging = true;
      startX = event.clientX;
      startFrame = frame;
      pointerId = event.pointerId;
      if (stage.setPointerCapture) stage.setPointerCapture(pointerId);
      markTouched();
    });

    stage.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      setFrame(startFrame + Math.round((event.clientX - startX) / dragStep));
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      if (pointerId !== null && stage.hasPointerCapture && stage.hasPointerCapture(pointerId)) {
        stage.releasePointerCapture(pointerId);
      }
      pointerId = null;
    }

    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', endDrag);

    // Native image drag would hijack the gesture on desktop.
    stage.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    stage.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') {
        setFrame(frame + 1);
        markTouched();
        event.preventDefault();
      }
      if (event.key === 'ArrowLeft') {
        setFrame(frame - 1);
        markTouched();
        event.preventDefault();
      }
    });

    show();
  }

  function AccordionItem(el) {
    this.el = el;
    this.summary = el.querySelector('summary');
    this.body = el.querySelector('.custom-protect__item-body, .custom-faq__item-body');
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;

    var self = this;
    this.summary.addEventListener('click', function (event) {
      event.preventDefault();
      self.onClick();
    });
  }

  AccordionItem.prototype.onClick = function () {
    this.el.style.overflow = 'hidden';
    if (this.isClosing || !this.el.open) {
      this.expand();
    } else if (this.isExpanding || this.el.open) {
      this.shrink();
    }
  };

  AccordionItem.prototype.shrink = function () {
    var self = this;
    this.isClosing = true;

    var startHeight = this.el.offsetHeight + 'px';

    // Measure the true collapsed height (item's own padding/border + summary only,
    // with the body's contribution removed) rather than assuming what it should be.
    this.body.style.display = 'none';
    var endHeight = this.el.offsetHeight + 'px';
    this.body.style.display = '';

    if (this.animation) this.animation.cancel();
    this.animation = this.el.animate(
      { height: [startHeight, endHeight] },
      { duration: 250, easing: 'ease-in-out' }
    );
    this.animation.onfinish = function () {
      self.onAnimationFinish(false);
    };
    this.animation.oncancel = function () {
      self.isClosing = false;
    };
  };

  AccordionItem.prototype.expand = function () {
    var self = this;
    this.el.style.height = this.el.offsetHeight + 'px';
    this.el.open = true;
    window.requestAnimationFrame(function () {
      self.grow();
    });
  };

  AccordionItem.prototype.grow = function () {
    var self = this;
    this.isExpanding = true;

    var startHeight = this.el.offsetHeight + 'px';

    // Measure the true expanded height (item's own padding/border + summary + body)
    // by briefly letting the box size naturally, then restore before animating so
    // there's no visible flash of the end state.
    this.el.style.height = 'auto';
    var endHeight = this.el.offsetHeight + 'px';
    this.el.style.height = startHeight;

    if (this.animation) this.animation.cancel();
    this.animation = this.el.animate(
      { height: [startHeight, endHeight] },
      { duration: 250, easing: 'ease-in-out' }
    );
    this.animation.onfinish = function () {
      self.onAnimationFinish(true);
    };
    this.animation.oncancel = function () {
      self.isExpanding = false;
    };
  };

  AccordionItem.prototype.onAnimationFinish = function (open) {
    this.el.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.el.style.height = '';
    this.el.style.overflow = '';
  };

  function initAccordion(section, itemSelector) {
    var items = [].slice.call(section.querySelectorAll(itemSelector));
    items.forEach(function (el) {
      new AccordionItem(el);
    });
  }

  function initSlider(section) {
    var slides = [].slice.call(section.querySelectorAll('.custom-slider__slide'));
    var dots = [].slice.call(section.querySelectorAll('.custom-slider__dot'));
    var prevBtn = section.querySelector('.custom-slider__nav--prev');
    var nextBtn = section.querySelector('.custom-slider__nav--next');
    if (slides.length < 2) return;

    var count = slides.length;
    var index = 0;

    function goTo(i) {
      index = ((i % count) + count) % count;
      slides.forEach(function (slide, slideIndex) {
        slide.classList.toggle('is-active', slideIndex === index);
      });
      dots.forEach(function (dot, dotIndex) {
        dot.classList.toggle('is-active', dotIndex === index);
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goTo(index - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goTo(index + 1);
      });
    }

    dots.forEach(function (dot, dotIndex) {
      dot.addEventListener('click', function () {
        goTo(dotIndex);
      });
    });
  }

  function initRoadTimeline(section) {
    var steps = [].slice.call(section.querySelectorAll('.custom-road__tstep'));
    if (!steps.length) return;

    var ticking = false;

    function update() {
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      var sectionRect = section.getBoundingClientRect();

      // Steps only reset in bulk once the whole section has scrolled below the
      // viewport — i.e. the user scrolled back up past it. Scrolling down past
      // the section (it sits above the viewport) leaves every step as-is.
      if (sectionRect.top > viewportHeight) {
        steps.forEach(function (step) {
          step.classList.remove('is-active');
        });
      } else {
        steps.forEach(function (step) {
          var rect = step.getBoundingClientRect();
          if (rect.top <= viewportHeight * 0.6) {
            step.classList.add('is-active');
          }
        });
      }

      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    update(); // set correct state on load (e.g. page refreshed mid-scroll)
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    steps.forEach(function (step) {
      step.addEventListener('mouseenter', function () {
        step.classList.add('is-active');
      });
    });
  }

  function initJoinMasonry(section) {
    var container = section.querySelector('.custom-join__bento');
    if (!container) return;

    var cells = [].slice.call(container.querySelectorAll('.custom-join__cell'));
    if (!cells.length) return;

    var resizeTimer = null;

    function columnCountFor() {
      return window.innerWidth <= 1024 ? 1 : 3;
    }

    // True masonry: each cell goes into whichever column is currently
    // shortest, so columns settle at different heights based on real content
    // — not CSS multi-column, which balances total height and just looks
    // like a static grid when cells are similar sizes.
    function layout() {
      var columnCount = columnCountFor();
      container.innerHTML = '';

      if (columnCount <= 1) {
        container.classList.remove('has-columns');
        cells.forEach(function (cell) {
          container.appendChild(cell);
        });
        return;
      }

      container.classList.add('has-columns');

      var columns = [];
      for (var i = 0; i < columnCount; i++) {
        var col = document.createElement('div');
        col.className = 'custom-join__col';
        container.appendChild(col);
        columns.push(col);
      }

      cells.forEach(function (cell) {
        var shortest = columns[0];
        columns.forEach(function (col) {
          if (col.offsetHeight < shortest.offsetHeight) shortest = col;
        });
        shortest.appendChild(cell);
      });
    }

    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(layout, 150);
    });

    layout();
  }

  function initHeroPopup(section) {
    var trigger = section.querySelector('.js-hero-popup-trigger');
    var dialog = section.querySelector('.js-hero-popup');
    if (!trigger || !dialog) return;

    var closeBtn = dialog.querySelector('.js-hero-popup-close');
    var video = dialog.querySelector('.js-hero-popup-video');
    var supportsDialog = typeof dialog.showModal === 'function';

    function openPopup() {
      if (supportsDialog) {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      if (video) {
        try {
          video.currentTime = 0;
        } catch (err) {
          // ignore - media not ready yet
        }
        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {
            // Autoplay with sound can be blocked by the browser; the user
            // still has visible controls to press play themselves.
          });
        }
      }
    }

    function closePopup() {
      if (video) video.pause();
      if (supportsDialog) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }

    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      openPopup();
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', function (event) {
        event.preventDefault();
        closePopup();
      });
    }

    // Click on the ::backdrop area lands on the <dialog> element itself
    // (its inner content is a separate child), so this closes on outside click.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closePopup();
    });

    dialog.addEventListener('cancel', function () {
      if (video) video.pause();
    });
  }

  function initScrollReveal(section, itemSelector) {
    var items = [].slice.call(section.querySelectorAll(itemSelector));
    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      // No IntersectionObserver support - just show everything rather than
      // leaving content permanently hidden.
      items.forEach(function (item) {
        item.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );

    items.forEach(function (item) {
      observer.observe(item);
    });
  }

  // Scroll reveal for [data-aos] elements — a small stand-in for the AOS
  // library with the same attribute API and none of the payload. See
  // _aos.scss for the effects and for why custom-protect and custom-road are
  // left on their own bespoke reveals.
  //
  // Stamping data-aos-ready on <html> is what tells the bootstrap in
  // theme.liquid that this file is alive; without it the bootstrap strips
  // .js-aos after 1.5s and everything renders unanimated but visible.
  //
  // Scroll position rather than IntersectionObserver, matching
  // initRoadTimeline below. IO looks like the obvious tool here and it is the
  // wrong one: a single instantaneous jump — an in-page anchor, a restored
  // scroll position on reload, a hard flick on a phone — can take an element
  // from below the viewport to above it in one frame. Its intersection ratio
  // is 0 before and 0 after, so the state never changes, so IO never fires,
  // and that section stays invisible for the rest of the visit. Comparing
  // rects catches anything at or above the trigger line, including things
  // already scrolled past.
  function initScrollAnimations(root) {
    document.documentElement.setAttribute('data-aos-ready', '');

    var pending = [].slice.call((root || document).querySelectorAll('[data-aos]'));
    if (!pending.length) return;

    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      pending.forEach(function (item) {
        item.classList.add('aos-animate');
      });
      return;
    }

    pending.forEach(function (item) {
      var delay = parseInt(item.getAttribute('data-aos-delay'), 10);
      if (delay > 0) item.style.transitionDelay = delay + 'ms';

      var duration = parseInt(item.getAttribute('data-aos-duration'), 10);
      if (duration > 0) item.style.transitionDuration = duration + 'ms';
    });

    var ticking = false;

    function update() {
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      var trigger = viewportHeight * 0.9; // reveal once the top edge is 10% in

      // Backwards, because revealed items are spliced out of the list.
      for (var i = pending.length - 1; i >= 0; i--) {
        if (pending[i].getBoundingClientRect().top > trigger) continue;
        pending[i].classList.add('aos-animate');
        pending.splice(i, 1);
      }

      // Everything has played once; stop reading rects on every scroll frame.
      if (!pending.length) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }

      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    // Two frames before the first pass, so the hidden starting state is
    // actually painted first. Called synchronously, anything already in view
    // would get its .aos-animate in the same frame as its opacity: 0 and snap
    // in with no transition at all.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(update);
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  function initTeamCards(section) {
    var toggles = [].slice.call(section.querySelectorAll('.custom-team__toggle'));
    toggles.forEach(function (toggle) {
      var card = toggle.closest('.custom-team__card');
      var wrap = card ? card.querySelector('.custom-team__bio-wrap') : null;
      if (!wrap) return;

      toggle.addEventListener('click', function () {
        var isOpen = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        wrap.classList.toggle('is-open', !isOpen);
      });
    });
  }

  // Klaviyo popup form behind every "Reserve Now" / "Reserve Yours Now" button.
  // In Klaviyo the form is a POPUP set to open on a custom trigger only, so it
  // never shows by itself — these buttons are the only way it opens.
  var RESERVE_KLAVIYO_FORM_ID = 'U9wqfv';
  var RESERVE_TRIGGER_SELECTOR =
    '.header-v2__cta, .header-v2__panel-reserve, .custom-hero__cta, .custom-closing__cta, .tuko-reserve-cta';

  function initReservePopup() {
    // One delegated listener instead of one per button, so buttons added or
    // re-rendered later (theme editor, section rendering) still open the form.
    document.addEventListener('click', function (event) {
      var trigger = event.target instanceof Element && event.target.closest(RESERVE_TRIGGER_SELECTOR);
      if (!trigger) return;

      // These CTAs point to href="#" (or a mailto fallback) — stop the jump
      // to the top / mail client and open the form instead.
      event.preventDefault();

      // The mobile menu's Reserve Now link sits inside the open menu panel —
      // close the panel so it isn't still open behind the popup.
      if (trigger.classList.contains('header-v2__panel-reserve')) {
        var header = trigger.closest('.header-v2');
        var panel = header && header.querySelector('.header-v2__panel');
        var menuBtn = header && header.querySelector('.header-v2__menu-btn');
        var backdrop = header && header.querySelector('.header-v2__backdrop');
        if (panel) panel.classList.remove('is-open');
        if (backdrop) backdrop.classList.remove('is-open');
        if (menuBtn) menuBtn.setAttribute('aria-expanded', 'false');
        document.documentElement.style.overflow = '';
      }

      // _klOnsite is Klaviyo's command queue: if klaviyo.js hasn't loaded yet,
      // the command waits and runs as soon as it does.
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', RESERVE_KLAVIYO_FORM_ID]);
    });
  }

  // Founding-place signups happen inside the Klaviyo popup above. Shopify's
  // Facebook & Instagram app runs Meta's pixel on this page (window.fbq) but only
  // reports store events, so it never sees a signup. Klaviyo announces each
  // signup as a 'klaviyoForms' window event; send Meta a Lead from it. Klaviyo
  // fires 'submit' once per form, on the email step; 'stepSubmit' would fire
  // again on the SMS step and double count, so it is ignored here. If fbq isn't
  // on the page (not loaded, or no marketing consent), nothing is sent.
  var META_PIXEL_ID = '909390118920873';

  function initLeadTracking() {
    var sent = false;

    window.addEventListener('klaviyoForms', function (event) {
      var detail = event.detail || {};
      if (sent || detail.type !== 'submit' || detail.formId !== RESERVE_KLAVIYO_FORM_ID) return;
      if (typeof window.fbq !== 'function') return;

      sent = true;
      // trackSingle: only TUKO's pixel, even if another pixel joins the page.
      // The event id lets a later server-side Lead be deduplicated.
      window.fbq('trackSingle', META_PIXEL_ID, 'Lead', {
        content_name: 'Founding place reservation',
        content_category: 'Reserve popup',
      }, {
        eventID: 'tuko-lead-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10),
      });
    });
  }

  function initBackToTop() {
    var button = document.querySelector('.back-to-top');
    if (!button) return;

    var ticking = false;

    function update() {
      ticking = false;
      var visible = window.scrollY > window.innerHeight;
      button.classList.toggle('is-visible', visible);
      // Out of the tab order while hidden, so keyboard users don't land on an
      // invisible button.
      button.tabIndex = visible ? 0 : -1;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }, { passive: true });
    window.addEventListener('resize', update);
    update();

    button.addEventListener('click', function () {
      var reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });

      // Send keyboard focus back to the top too, otherwise the next Tab jumps
      // straight back down to the footer.
      var main = document.getElementById('MainContent');
      if (main) {
        if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
      }
    });
  }

  function init() {
    initScrollAnimations();

    document.querySelectorAll('.header-v2').forEach(function (header) {
      if (header.classList.contains('header-v2--sticky')) {
        initStickyHeader(header);
      }
      initMenuPanel(header);
    });

    document.querySelectorAll('.tuko-adventure').forEach(function (section) {
      initAdventureViewer(section);
    });

    document.querySelectorAll('.custom-hero').forEach(function (section) {
      initHeroPopup(section);
    });

    initReservePopup();
    initLeadTracking();
    initBackToTop();

    document.querySelectorAll('.custom-protect').forEach(function (section) {
      initScrollReveal(section, '.js-scroll-reveal');
    });

    document.querySelectorAll('.custom-faq').forEach(function (section) {
      initAccordion(section, '.custom-faq__item');
    });

    document.querySelectorAll('.custom-slider').forEach(function (section) {
      initSlider(section);
    });

    document.querySelectorAll('.custom-road').forEach(function (section) {
      initRoadTimeline(section);
    });

    document.querySelectorAll('.custom-join').forEach(function (section) {
      initJoinMasonry(section);
    });

    document.querySelectorAll('.custom-team').forEach(function (section) {
      initTeamCards(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor swaps out a whole section's markup when a merchant edits
  // it. Its [data-aos] elements arrive hidden and unobserved, so without this
  // the section they are editing would disappear on them.
  document.addEventListener('shopify:section:load', function (event) {
    initScrollAnimations(event.target);
  });
})();